import React, { useEffect, useState } from "react";
import { Users, CalendarDays, Newspaper, Mail, Star, History, BellRing, Loader2, Gift } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [log, setLog] = useState([]);
  const [renewals, setRenewals] = useState([]);
  const [sending, setSending] = useState(false);
  const [mship, setMship] = useState({ annual_free_months: 2, referral_free_months: 1 });
  const [savingMship, setSavingMship] = useState(false);

  const loadRenewals = () => api.get("/admin/renewals").then((r) => setRenewals(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/changelog?limit=8").then((r) => setLog(r.data)).catch(() => {});
    api.get("/admin/settings/membership").then((r) => { if (r.data && Object.keys(r.data).length) setMship({ annual_free_months: r.data.annual_free_months ?? 2, referral_free_months: r.data.referral_free_months ?? 1 }); }).catch(() => {});
    loadRenewals();
  }, []);

  const saveMship = async () => {
    setSavingMship(true);
    try {
      await api.put("/admin/settings/membership", { value: { annual_free_months: Number(mship.annual_free_months), referral_free_months: Number(mship.referral_free_months) } });
      toast.success("Membership settings saved");
    } catch (e) { toast.error("Could not save settings"); }
    finally { setSavingMship(false); }
  };

  const sendReminders = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/renewals/send");
      if (data.sent > 0) toast.success(`Sent ${data.sent} renewal reminder(s)`);
      else if (data.checked === 0) toast.info("No memberships are due for renewal right now.");
      else toast.info(`Checked ${data.checked}, but email isn't configured — ${data.failed} not sent. Set up SMTP in Integrations.`);
      loadRenewals();
    } catch (e) {
      toast.error("Could not send reminders");
    } finally {
      setSending(false);
    }
  };

  const cards = [
    { label: "Members", value: stats?.members, Icon: Users },
    { label: "Events", value: stats?.events, Icon: CalendarDays },
    { label: "Media items", value: stats?.media, Icon: Newspaper },
    { label: "New messages", value: stats?.contacts_new, Icon: Mail },
    { label: "Spotlights", value: stats?.spotlights, Icon: Star },
  ];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-display text-3xl md:text-4xl font-medium text-primary mb-6">Dashboard</h1>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="bg-card border border-primary/10 rounded-2xl p-5">
            <div className="w-10 h-10 rounded-full bg-secondary/15 text-secondary flex items-center justify-center mb-3"><c.Icon size={18} /></div>
            <p className="font-display text-4xl font-semibold text-primary">{c.value ?? "—"}</p>
            <p className="text-sm text-muted-foreground">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="mt-10 bg-card border border-primary/10 rounded-2xl p-6" data-testid="admin-membership-settings">
        <div className="flex items-center gap-2 mb-1"><Gift size={18} className="text-secondary" /><h2 className="font-display text-2xl font-semibold text-primary">Membership & referrals</h2></div>
        <p className="text-sm text-muted-foreground mb-5">Control the yearly discount and the referral reward. Changes apply to new signups and referrals immediately.</p>
        <div className="grid sm:grid-cols-2 gap-6 max-w-lg">
          <div>
            <label className="text-sm font-medium text-primary">Free months on annual plan</label>
            <input type="number" min="0" max="11" data-testid="mship-annual-input" value={mship.annual_free_months} onChange={(e) => setMship({ ...mship, annual_free_months: e.target.value })} className="mt-1.5 w-full h-11 rounded-xl border border-primary/15 bg-muted/40 px-4" />
            <p className="text-xs text-muted-foreground mt-1">Yearly = pay for {Math.max(1, 12 - Number(mship.annual_free_months || 0))} months.</p>
          </div>
          <div>
            <label className="text-sm font-medium text-primary">Free months per referral</label>
            <input type="number" min="0" max="12" data-testid="mship-referral-input" value={mship.referral_free_months} onChange={(e) => setMship({ ...mship, referral_free_months: e.target.value })} className="mt-1.5 w-full h-11 rounded-xl border border-primary/15 bg-muted/40 px-4" />
            <p className="text-xs text-muted-foreground mt-1">Credited to the referrer when a friend joins.</p>
          </div>
        </div>
        <button onClick={saveMship} disabled={savingMship} data-testid="mship-save-btn" className="mt-5 rounded-full bg-primary text-primary-foreground px-6 py-2.5 text-sm font-semibold hover:bg-secondary transition-colors flex items-center gap-2 disabled:opacity-60">
          {savingMship && <Loader2 size={14} className="animate-spin" />} Save settings
        </button>
      </div>

      <div className="mt-10 bg-card border border-primary/10 rounded-2xl p-6" data-testid="admin-renewals-panel">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-2"><BellRing size={18} className="text-secondary" /><h2 className="font-display text-2xl font-semibold text-primary">Upcoming renewals</h2></div>
          <button onClick={sendReminders} disabled={sending} data-testid="renewals-send-btn"
            className="rounded-full bg-primary text-primary-foreground px-5 py-2 text-sm font-semibold hover:bg-secondary transition-colors flex items-center gap-2 disabled:opacity-60">
            {sending && <Loader2 size={14} className="animate-spin" />} Send reminders now
          </button>
        </div>
        {renewals.length === 0 ? (
          <p className="text-muted-foreground">No paid memberships are due to renew in the next 30 days.</p>
        ) : (
          <div className="space-y-2">
            {renewals.map((r) => (
              <div key={r.id} className="flex items-center gap-3 text-sm border-b border-primary/5 pb-2">
                <span className="text-primary font-medium flex-1 truncate">{r.name} · {r.email}</span>
                <span className="text-xs uppercase tracking-wide font-semibold text-secondary capitalize">{r.tier?.replace("_", " ")}</span>
                <span className="text-muted-foreground text-xs w-24 text-right">{(r.renews_at || "").slice(0, 10)}</span>
                <span className={`text-xs w-20 text-right ${r.reminded_at ? "text-emerald-600" : "text-muted-foreground/60"}`}>{r.reminded_at ? "reminded" : "—"}</span>
              </div>
            ))}
          </div>
        )}
        <p className="text-[11px] text-muted-foreground mt-3">Reminders also send automatically each day for members renewing within 3 days.</p>
      </div>

      <div className="mt-10 bg-card border border-primary/10 rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4"><History size={18} className="text-secondary" /><h2 className="font-display text-2xl font-semibold text-primary">Recent activity</h2></div>
        <div className="space-y-3">
          {log.map((l) => (
            <div key={l.id} className="flex items-center gap-3 text-sm border-b border-primary/5 pb-3">
              <span className="text-xs uppercase tracking-wide font-semibold text-secondary w-16 shrink-0">{l.action}</span>
              <span className="text-primary flex-1">{l.summary}</span>
              <span className="text-muted-foreground text-xs">{new Date(l.timestamp).toLocaleString()}</span>
            </div>
          ))}
          {log.length === 0 && <p className="text-muted-foreground">No activity yet.</p>}
        </div>
      </div>
    </div>
  );
}
